import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import type { ActivityLogEntry } from '@/shared/db';

interface ActivityTableProps {
  activities: ActivityLogEntry[];
  loading?: boolean;
}

export const ActivityTable: React.FC<ActivityTableProps> = ({
  activities,
  loading,
}) => {
  const { t } = useTranslation();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const formatAction = (action: string) => {
    const actionKey = `admin.activity.actions.${action}`;
    const translated = t(actionKey);
    // If translation doesn't exist, return formatted action
    if (translated === actionKey) {
      return action
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    }
    return translated;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500"></div>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-juridico/60">{t('admin.activity.noResults')}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-romance-200">
        <thead className="bg-romance-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
              {t('admin.activity.table.admin')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
              {t('admin.activity.table.action')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
              {t('admin.activity.table.entity')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
              {t('admin.activity.table.date')}
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-juridico uppercase tracking-wider">
              {t('admin.activity.table.details')}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-romance-200">
          {activities.map((activity) => {
            const isExpanded = expandedRows.has(activity.id);
            return (
              <React.Fragment key={activity.id}>
                <motion.tr
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-romance-50 transition-colors cursor-pointer"
                  onClick={() => toggleRow(activity.id)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-juridico">
                        {activity.admin_name || 'Sistema'}
                      </div>
                      {activity.admin_email && (
                        <div className="text-sm text-juridico/60">
                          {activity.admin_email}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-tiber-500/10 text-tiber-500">
                      {formatAction(activity.action)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-juridico capitalize">
                      {activity.entity_type}
                    </div>
                    {activity.entity_id && (
                      <div className="text-xs text-juridico/60 font-mono">
                        {activity.entity_id.substring(0, 8)}...
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-juridico/60">
                    {formatDate(activity.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button className="text-tiber-500 hover:text-tiber-500/80 transition-colors">
                      {isExpanded ? (
                        <ChevronUpIcon className="w-5 h-5" />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5" />
                      )}
                    </button>
                  </td>
                </motion.tr>
                <AnimatePresence>
                  {isExpanded && (
                    <motion.tr
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="bg-romance-50"
                    >
                      <td colSpan={5} className="px-6 py-4">
                        <div className="space-y-3">
                          <div className="flex items-start space-x-2">
                            <InformationCircleIcon className="w-5 h-5 text-tiber-500 mt-0.5" />
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-juridico mb-2">
                                {t('admin.activity.table.details')}
                              </h4>
                              <div className="bg-white rounded-lg p-4 border border-romance-200">
                                <pre className="text-xs text-juridico whitespace-pre-wrap font-mono">
                                  {JSON.stringify(activity.details, null, 2)}
                                </pre>
                              </div>
                            </div>
                          </div>
                          {activity.ip_address && (
                            <div className="text-xs text-juridico/60">
                              <span className="font-medium">IP:</span> {activity.ip_address}
                            </div>
                          )}
                          {activity.user_agent && (
                            <div className="text-xs text-juridico/60">
                              <span className="font-medium">User Agent:</span>{' '}
                              {activity.user_agent}
                            </div>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  )}
                </AnimatePresence>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

